import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Pagination,
  InputAdornment,
  Chip,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { PartInfo, CatalogResponse, CatalogFilters } from '../types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCatalog } from '../services/api';
import { formatNumber } from '../utils/formatters';
import { CategoryFilter } from '../components/CategoryFilter';

export const CatalogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalogData, setCatalogData] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 12;
  const navigate = useNavigate();

  const [filters, setFilters] = useState<CatalogFilters>(() => ({
    search: searchParams.get('search') || undefined,
    category: searchParams.get('category') || 'Engine',
    subCategory: searchParams.get('subCategory') || undefined
  }));

  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });

  const isSearching = !!(filters.search?.trim());
  const isFiltering = !!(filters.category || filters.subCategory);
  const hasActiveFilters = isSearching || isFiltering;

  const updateFiltersAndUrl = useCallback((newFilters: CatalogFilters, page: number = 1) => {
    setFilters(newFilters);
    setCurrentPage(page);

    const params = new URLSearchParams();

    if (newFilters.search?.trim()) {
      params.set('search', newFilters.search.trim());
    }
    if (newFilters.category) {
      params.set('category', newFilters.category);
    }
    if (newFilters.subCategory) {
      params.set('subCategory', newFilters.subCategory);
    }
    if (page > 1) {
      params.set('page', page.toString());
    }

    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchCatalog = useCallback(async (page: number, currentFilters: CatalogFilters) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getCatalog(page, itemsPerPage, currentFilters);
      setCatalogData(data);
    } catch (err) {
      setError('Failed to load catalog. Please try again.');
      console.error('Error fetching catalog:', err);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCatalog(currentPage, filters);
    }, filters.search ? 500 : 0); // Only debounce search queries

    return () => clearTimeout(timeoutId);
  }, [fetchCatalog, currentPage, filters]);

  const displayParts = catalogData?.parts || [];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = {
      ...filters,
      search: event.target.value || undefined
    };
    updateFiltersAndUrl(newFilters, 1); // Reset to page 1 when searching
  };

  const handleFiltersChange = (newFilters: CatalogFilters) => {
    updateFiltersAndUrl(newFilters, 1); // Reset to page 1 when filtering
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    updateFiltersAndUrl(filters, page); // Update URL with new page
  };

  const handlePartClick = (part: PartInfo) => {
    const currentLocation = window.location.pathname + window.location.search;
    navigate(`/part/${part.id}`, { 
      state: { 
        part,
        returnTo: currentLocation
      } 
    });
  };

  const getResultsDescription = () => {
    if (!catalogData) return '';

    const { totalCount, parts } = catalogData;
    const activeFilterCount = [filters.search, filters.category, filters.subCategory].filter(Boolean).length;

    if (activeFilterCount === 0) {
      return `📋 Showing ${formatNumber(parts.length)} of ${formatNumber(totalCount)} parts`;
    }

    let description = `🔍 Found ${formatNumber(totalCount)} matching parts`;
    const filterDetails = [];
    
    if (filters.search) {
      filterDetails.push(`search: "${filters.search}"`);
    }
    if (filters.category) {
      filterDetails.push(`category: "${filters.category}"`);
    }
    if (filters.subCategory) {
      filterDetails.push(`subcategory: "${filters.subCategory}"`);
    }

    if (filterDetails.length > 0) {
      description += ` for ${filterDetails.join(', ')}`;
    }

    return description;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: '1.8rem', sm: '2.125rem' } }}
          >
            Catalog
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            Browse and search available parts
          </Typography>
        </Box>

        {/* Searching */}
        <Box sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: '100%'
        }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by part name, supplier code, description..."
            value={filters.search || ''}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color={isSearching ? "primary" : "inherit"} />
                  </InputAdornment>
                ),
              }
            }}
            sx={{
              maxWidth: { xs: '100%', sm: 600 },
              display: 'block',
              '& .MuiOutlinedInput-root': {
                backgroundColor: isSearching ? 'primary.50' : 'inherit',
                borderColor: isSearching ? 'primary.main' : 'inherit'
              }
            }}
          />

          <CategoryFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            disabled={loading}
          />
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Results Info */}
        {catalogData && !loading && (
          <Box sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            <Typography variant="body1" color="text.secondary">
              {getResultsDescription()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Page {formatNumber(currentPage)} of {formatNumber(catalogData.totalPages)}
            </Typography>
          </Box>
        )}

        {/* Parts Grid */}
        {!loading && catalogData && displayParts.length > 0 ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(280px, 1fr))',
              md: 'repeat(auto-fill, minmax(300px, 1fr))'
            },
            gap: { xs: 2, sm: 3 }
          }}>
            {displayParts.map((part) => (
              <Card key={part.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => handlePartClick(part)} sx={{ flexGrow: 1 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={part.imageUrl}
                    alt={part.name}
                    sx={{ objectFit: 'contain' }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.2
                      }}
                    >
                      {part.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3,
                        mb: 1
                      }}
                    >
                      {part.descriptionProdDescriptionInvoice}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      {part.supplierName} ({part.supplierCode})
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        mt: 2,
                        flexWrap: 'wrap',
                        gap: 0.5,
                        '& .MuiChip-root': {
                          fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                          height: { xs: 24, sm: 32 }
                        }
                      }}
                    >
                      {part.productCategory && (
                        <Chip
                          label={part.productCategory}
                          size="small"
                          variant="outlined"
                          color={filters.category === part.productCategory ? "primary" : "default"}
                        />
                      )}
                      {part.productSubCategory && (
                        <Chip
                          label={part.productSubCategory}
                          size="small"
                          variant="outlined"
                          color={filters.subCategory === part.productSubCategory ? "secondary" : "default"}
                        />
                      )}
                      {part.viparProductLevel && (
                        <Chip
                          label={part.viparProductLevel}
                          size="small"
                          color="primary"
                        />
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
               </Card>
             ))}
           </Box>
                  ) : (
            !loading && catalogData && (
              <Box sx={{
                textAlign: 'center',
                py: { xs: 4, sm: 8 },
                px: { xs: 2, sm: 0 }
              }}>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {hasActiveFilters ? 'No parts found matching your criteria' : 'No parts available'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mt: 1 }}
                >
                  {hasActiveFilters && 'Try adjusting your search terms or filters'}
                </Typography>
              </Box>
            )
          )}

        {/* Pagination */}
        {!loading && catalogData && catalogData.totalPages > 1 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 0 }
          }}>
            <Pagination
              count={catalogData.totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};
